import { IStartTime } from 'static/interfaces'
import { IDate } from './useCalendarEvents'
import { getPriceBreakdown, IPostPrice, ISelectedGuests, isStartTimeAvailable } from './reservation'

interface IMemberPost {
  id: number | string
  post_type: string
  post_prices: IPostPrice[]
}

interface IReservationPostOption<T> {
  memberPost: T
  totalPrice: number
}

interface IReservationStartTimeGroup<T> extends IReservationPostOption<T> {
  postId: number
  startTimes: IStartTime[]
  earliestAvailableStartTimeMinutes: number
}

/**
 * Total Price は、通常ツアーでは選択した大人・子ども・幼児の料金合計、
 * プライベートツアーでは既存の人数別料金設定で選ばれるグループ料金とする。
 * 料金は日付・時間帯によらないため、子ツアーごとに一度だけ計算する。
 */
export const getReservationPostOptions = <T extends IMemberPost>(
  memberPosts: T[],
  guests: ISelectedGuests
): Map<string, IReservationPostOption<T>> => {
  const options = new Map<string, IReservationPostOption<T>>()
  memberPosts.forEach(memberPost => {
    const isPrivate = memberPost.post_type === 'private'
    const prices = getPriceBreakdown(memberPost.post_prices, guests, isPrivate)
    const totalPrice = isPrivate
      ? prices.private.price
      : prices.adult.calcPrice + prices.child.calcPrice + prices.infant.calcPrice
    options.set(String(memberPost.id), { memberPost, totalPrice })
  })
  return options
}

/**
 * 予約一覧の表示仕様：
 * - postOptions に含まれる子ツアーのうち、その日付に予約可能な時間枠が
 *   1件以上ある子ツアーだけカードを表示する。限定公開による追加の除外はしない。
 * - 表示するカード内には、予約不可の時間枠も残す。
 * - 表示するカードが0件の日付は、日付見出しも含めて表示しない。
 * - 同じ日付内のカードは Total Price が安いものを先に表示する。
 *   同額なら、予約可能な時間枠のうち最も早い催行時間を持つカードを先に表示する。
 *   その時間も同じなら、子ツアーIDが小さいカードを先に表示する。
 * 予約可能かどうかは isStartTimeAvailable の条件に従う。
 */
export const getAvailableReservationDates = <T>(
  dates: IDate[],
  postOptions: Map<string, IReservationPostOption<T>>,
  guestCount: number
) =>
  dates
    .map(date => {
      const groups = new Map<number, IReservationStartTimeGroup<T>>()
      const startTimes = date.startTimes || []
      // 全時間枠を一度だけ走査する。カード内の時間順ソートは表示するカード側で行う。
      startTimes.forEach((startTime: IStartTime) => {
        let group = groups.get(startTime.post_id)
        if (!group) {
          const option = postOptions.get(String(startTime.post_id))
          if (!option) {
            return
          }
          group = {
            ...option,
            postId: startTime.post_id,
            startTimes: [],
            earliestAvailableStartTimeMinutes: Infinity,
          }
          groups.set(startTime.post_id, group)
        }
        group.startTimes.push(startTime)
        if (isStartTimeAvailable(startTime, guestCount)) {
          const [hours, minutes] = startTime.start_time.split(':').map(Number)
          group.earliestAvailableStartTimeMinutes = Math.min(
            group.earliestAvailableStartTimeMinutes,
            hours * 60 + minutes
          )
        }
      })
      const startTimeGroups = Array.from(groups.values())
        .filter(group => group.earliestAvailableStartTimeMinutes !== Infinity)
        .sort(
          (a, b) =>
            a.totalPrice - b.totalPrice ||
            a.earliestAvailableStartTimeMinutes - b.earliestAvailableStartTimeMinutes ||
            a.postId - b.postId
        )
      return { ...date, startTimeGroups }
    })
    .filter(date => date.startTimeGroups.length > 0)
